import { useEffect, useState, useCallback, useRef } from 'react';
import clienteAxios from '../config/axios';
import { useT } from 'i18n';

const POR_PAGINA = 20;
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoy = () => new Date().toISOString().slice(0, 10);

const TIPOS = [
  { id: '', clave: 'movs.todos' },
  { id: 'ingresos', clave: 'movs.ingresos' },
  { id: 'egresos', clave: 'movs.egresos' },
];

const celdaCsv = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const Movimientos = () => {
  const { t, ts } = useT();
  const [filtros, setFiltros] = useState({ desde: '', hasta: '', tipo: '', q: '' });
  const [q, setQ] = useState('');
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState({ data: [], total: 0, ingresos: 0, egresos: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [exportando, setExportando] = useState(false);
  const peticion = useRef(0);

  // la busqueda de texto espera 400 ms para no consultar en cada tecla
  useEffect(() => {
    const t = setTimeout(() => { setFiltros((f) => (f.q === q ? f : { ...f, q })); setPagina(1); }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const cargar = useCallback(async () => {
    const id = ++peticion.current;
    setCargando(true);
    setError('');
    try {
      const r = await clienteAxios.post('/api/client/balance/historial', { ...filtros, page: pagina, por_pagina: POR_PAGINA });
      if (id === peticion.current) setDatos(r.data);
    } catch (e) {
      if (id === peticion.current) setError(e.response?.data?.message ? { msg: e.response.data.message } : { clave: 'movs.errCargar' });
    } finally {
      if (id === peticion.current) setCargando(false);
    }
  }, [filtros, pagina]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (campo, valor) => { setFiltros((f) => ({ ...f, [campo]: valor })); setPagina(1); };
  const limpiar = () => { setQ(''); setFiltros({ desde: '', hasta: '', tipo: '', q: '' }); setPagina(1); };
  const hayFiltros = filtros.desde || filtros.hasta || filtros.tipo || filtros.q;

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await clienteAxios.post('/api/client/balance/historial', { ...filtros, todo: 1 });
      const filas = [[t('movs.colFecha'), t('movs.colReferencia'), t('movs.colDescripcion'), t('movs.csvMonto'), t('movs.csvSaldo')]]
        .concat(r.data.data.map((m) => [m.fecha, m.codigo || '', m.descripcion || '', m.monto.toFixed(2), m.saldo.toFixed(2)]));
      const csv = '﻿' + filas.map((f) => f.map(celdaCsv).join(',')).join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('movs.archivo')}-${hoy()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError({ clave: 'movs.errExportar' });
    } finally {
      setExportando(false);
    }
  };

  const paginas = Math.max(1, Math.ceil(datos.total / POR_PAGINA));

  return (
    <div className="content p7-mov">
      <div className="p7-mov-head">
        <div>
          <h2>{t('movs.titulo')}</h2>
          <p>{t('movs.sub')}</p>
        </div>
        <button type="button" className="p7-btn-gold" onClick={exportar} disabled={exportando || datos.total === 0}>
          {exportando ? t('movs.preparando') : t('movs.exportar')}
        </button>
      </div>

      <div className="p7-mov-resumen">
        <div><span>{t('movs.ingresos')}</span><strong className="p7-pos">+{fmt(datos.ingresos)}</strong></div>
        <div><span>{t('movs.egresos')}</span><strong className="p7-neg">{fmt(datos.egresos)}</strong></div>
        <div><span>{t('movs.cantidad')}</span><strong>{datos.total}</strong></div>
      </div>

      <div className="p7-mov-filtros">
        <div className="p7-seg" role="group" aria-label={t('movs.tipoAria')}>
          {TIPOS.map((tp) => (
            <button key={tp.id} type="button" className={filtros.tipo === tp.id ? 'on' : ''} onClick={() => cambiar('tipo', tp.id)}>{t(tp.clave)}</button>
          ))}
        </div>
        <label>{t('movs.desde')}<input type="date" value={filtros.desde} max={filtros.hasta || hoy()} onChange={(e) => cambiar('desde', e.target.value)} /></label>
        <label>{t('movs.hasta')}<input type="date" value={filtros.hasta} min={filtros.desde} max={hoy()} onChange={(e) => cambiar('hasta', e.target.value)} /></label>
        <input type="search" className="p7-mov-buscar" placeholder={t('movs.buscarPh')} value={q} maxLength={40} onChange={(e) => setQ(e.target.value)} aria-label={t('movs.buscarAria')} />
        {hayFiltros && <button type="button" className="p7-btn-ghost" onClick={limpiar}>{t('movs.limpiar')}</button>}
      </div>

      {error && <div className="p7-wallet-error">{error.clave ? t(error.clave) : ts(error.msg)}</div>}

      <div className="p7-table-wrap" aria-busy={cargando}>
        <table className="p7-table">
          <thead>
            <tr><th>{t('movs.colFecha')}</th><th>{t('movs.colReferencia')}</th><th>{t('movs.colDescripcion')}</th><th className="p7-num">{t('movs.colMonto')}</th><th className="p7-num">{t('movs.colSaldo')}</th></tr>
          </thead>
          <tbody style={{ opacity: cargando ? 0.5 : 1 }}>
            {datos.data.map((m) => (
              <tr key={m.id}>
                <td>{m.fecha}</td>
                <td className="p7-mono">{m.codigo || '—'}</td>
                <td className="p7-mov-td-desc">{m.descripcion}</td>
                <td className={`p7-num ${m.monto < 0 ? 'p7-neg' : 'p7-pos'}`}>{m.monto > 0 ? '+' : ''}{fmt(m.monto)}</td>
                <td className="p7-num">{fmt(m.saldo)}</td>
              </tr>
            ))}
            {!cargando && datos.data.length === 0 && (
              <tr><td colSpan="5"><div className="p7-empty"><p>{hayFiltros ? t('movs.sinResultados') : t('movs.sinFiltros')}</p>{hayFiltros && <button type="button" className="p7-btn-ghost" onClick={limpiar}>{t('movs.quitarFiltros')}</button>}</div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="p7-mov-pie">
        <span>{t('movs.pagina', { p: pagina, n: paginas })}</span>
        <div>
          <button type="button" className="p7-btn-ghost" disabled={pagina <= 1 || cargando} onClick={() => setPagina(pagina - 1)}>{t('movs.anterior')}</button>
          <button type="button" className="p7-btn-ghost" disabled={pagina >= paginas || cargando} onClick={() => setPagina(pagina + 1)}>{t('movs.siguiente')}</button>
        </div>
      </div>
    </div>
  );
};

export default Movimientos;
